'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Checkbox, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import { useSearchParams } from 'next/navigation';
import { FC, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { NavigationTabs } from '../../../../../../../../components/common';
import { RestorePasswordModal } from '../../../../../../../../components/login/RestorePasswordModal';
import { urls } from '../../../../../../../../constants/v2/urls';
import { useAuth } from '../../../../../../../../hooks/auth/use-auth';
import { useBoolean } from '../../../../../../../../hooks/use-boolean';
import { useLocale } from '../../../../../../../../hooks/use-locale';
import { useTranslations } from '../../../../../../../../hooks/use-translations';
import { useRouter } from '../../../../../../../../i18n/routing';
import { show2FaModal } from '../../../../../../../../store/vars';

import styles from './Login.module.scss';

type LogInForm = {
  username: string;
  password: string;
  rememberMe: boolean;
  token?: string;
};

const schema = yup
  .object({
    username: yup.string().required(),
    password: yup.string().required(),
    token: yup.string(),
    rememberMe: yup.boolean(),
  })
  .required();

export const Login: FC = () => {
  const router = useRouter();
  const t = useTranslations('pages.account.login');
  const [is2Fa, setIs2Fa] = useState(false);
  const { login } = useAuth(is2Fa);

  const {
    register,
    handleSubmit,
    watch,
    setFocus,
    formState: { errors, isDirty, isValid },
  } = useForm<LogInForm>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });
  const [errorAuthApi, setErrorAuthApi] = useState(false);
  const {
    value: restorePasswordModalOpen,
    setTrue: openRestorePasswordModal,
    setFalse: closeRestorePasswordModal,
  } = useBoolean(false);

  const locale = useLocale();
  const searchParams = useSearchParams();

  const onSubmit: SubmitHandler<LogInForm> = async (data) => {
    const resultLogin = await login(data);
    const is2FaError = resultLogin.customErrors?.includes('2fa');

    if (is2FaError) {
      setIs2Fa(true);
    }

    if (resultLogin.errors || resultLogin.customErrors?.length) {
      setFocus('password');
      setErrorAuthApi(!is2FaError);

      return;
    }

    router.push({
      pathname: searchParams?.get('returnUrl') ?? urls.Dashboard,
    });

    if (!data.token) {
      show2FaModal(true);
    }
  };

  /* Что бы не делать кучу перерендеров страницы, делаем через useEffect */
  useEffect(() => {
    const unsubscriber = watch(() => {
      /* сбрасываем ошибку аутентификации после изменения значений формы */
      setErrorAuthApi(false);
    });

    return () => unsubscriber.unsubscribe();
  }, [watch]);

  return (
    <div>
      <NavigationTabs activeTab="login" tabs={[{ code: 'login', name: t('meta.title') }]} />

      <Grid container spacing={2}>
        <Grid item md={6} sm={12} xs={12}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item md={is2Fa ? 4 : 6} sm={12} xs={12}>
                <TextField
                  {...register('username', { required: true })}
                  placeholder=""
                  label={t('form.email')}
                  error={errors.username != null}
                  helperText={errors.username?.message ?? ' '}
                />
              </Grid>

              <Grid item md={is2Fa ? 4 : 6} sm={12} xs={12}>
                <TextField
                  {...register('password', { required: true })}
                  placeholder=""
                  label={t('form.password')}
                  type="password"
                  error={errors.password != null}
                  helperText={errors.password?.message ?? ' '}
                />
              </Grid>

              {is2Fa && (
                <Grid item md={4} sm={12} xs={12}>
                  <TextField
                    {...register('token', { required: true })}
                    placeholder=""
                    label={t('form.2faToken')}
                    type="text"
                    error={errors.token != null}
                    helperText={errors.token?.message ?? ' '}
                  />
                </Grid>
              )}

              <Grid item md={6} sx={{ textAlign: 'center' }} sm={8} xs={8}>
                {!isDirty || !isValid || errorAuthApi ? (
                  <Typography
                    color="text.secondary"
                    fontWeight={700}
                    style={{ cursor: 'pointer' }}
                    onClick={openRestorePasswordModal}
                  >
                    {t('link.forgotPassword')}
                  </Typography>
                ) : (
                  <FormControlLabel label={t('form.remember')} control={<Checkbox {...register('rememberMe')} />} />
                )}
              </Grid>

              <Grid item md={6} sm={4} xs={4}>
                <Button type="submit" className={styles.submitButton}>
                  {t(!isDirty || !isValid || errorAuthApi ? 'form.submitFirst' : 'form.submitGood')}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Grid>

        <Grid item md>
          <Typography color="text.secondary">{t('text.terms')}</Typography>

          <Typography color="text.secondary">
            {t('text.useCookie')}
            <a href={`/${locale}/doc/privacy`} style={{ marginLeft: '3px' }}>
              {t('link.privacyPolicy')}
            </a>
            .
          </Typography>
        </Grid>
      </Grid>

      <RestorePasswordModal open={restorePasswordModalOpen} onClose={closeRestorePasswordModal} />
    </div>
  );
};
